import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import {
    ResetDocViewerReportState,
    ResetState,
    SetCurrentDocument,
    SetCurrentReport,
    SetCurrentReportMode,
    SetCurrentReportPage,
    SetDocumentCollection,
    SetDocViewerReportCollectionConfig,
    SetDocViewerReportData,
    SetDocViewerRestoreDataMode,
    SetMergeDoc,
    SetStaticDoc
} from './document-viewer-object.actions';

export interface DocumentViewerObjectStateModel {
    currentDoc: any;
    report: any;
    documentCollection: [];
    currentReportMode: boolean;
    mergeDoc: any;
    currentReportPage: any;
    reportData: any;
    reportCollectionConfig: any;
    restoreDataMode: boolean;
    staticDoc: any;
}

const DocumentViewerObjectStateDefaults: DocumentViewerObjectStateModel = {
    currentDoc: null,
    report: null,
    documentCollection: [],
    currentReportMode: false,
    mergeDoc: null,
    currentReportPage: null,
    reportData: null,
    reportCollectionConfig: null,
    restoreDataMode: false,
    staticDoc: null
};

@State<DocumentViewerObjectStateModel>({
    name: 'DocumentViewerObjectState',
    defaults: DocumentViewerObjectStateDefaults
})
@Injectable()
export class DocumentViewerObjectState {
    constructor(private helper: StateHelperService, private app: AppService, private store: Store) { }

    //************** SELECTORS  ***************

    @Selector()
    static documents(state: DocumentViewerObjectStateModel) {
        return state.documentCollection;
    }

    @Selector()
    static documentCount(state: DocumentViewerObjectStateModel) {
        return state.documentCollection.length;
    }

    @Selector()
    static currentDoc(state: DocumentViewerObjectStateModel) {
        return state.currentDoc;
    }

    @Selector()
    static currentDocID(state: DocumentViewerObjectStateModel) {
        if (state.currentDoc && state.currentDoc.Doc_ID) {
            return state.currentDoc.Doc_ID;
        } else {
            return null;
        }
    }

    @Selector()
    static report(state: DocumentViewerObjectStateModel) {
        return state.report;
    }

    @Selector()
    static currentReportMode(state: DocumentViewerObjectStateModel) {
        return state.currentReportMode;
    }

    @Selector()
    static currentDocProtectedInd(state: DocumentViewerObjectStateModel) {
        return state.currentDoc.Protected_Ind;
    }

    @Selector()
    static mergeDoc(state: DocumentViewerObjectStateModel) {
        return state.mergeDoc;
    }

    @Selector()
    static staticDoc(state: DocumentViewerObjectStateModel) {
        return state.staticDoc;
    }

    @Selector()
    static currentReportPage(state: DocumentViewerObjectStateModel) {
        return state.currentReportPage;
    }

    @Selector()
    static reportData(state: DocumentViewerObjectStateModel) {
        return state.reportData;
    }

    @Selector()
    static reportCollectionConfig(state: DocumentViewerObjectStateModel) {
        return state.reportCollectionConfig;
    }

    @Selector()
    static restoreDataMode(state: DocumentViewerObjectStateModel) {
        return state.restoreDataMode;
    }

    //******  ACTIONS  ******

    @Action(ResetState)
    resetState(sc: StateContext<DocumentViewerObjectStateModel>, action: ResetState) {
        sc.patchState({
            currentDoc: null,
            report: null,
            documentCollection: [],
            currentReportMode: false,
            mergeDoc: null,
            currentReportPage: null,
            reportCollectionConfig: null,
            reportData: null,
            restoreDataMode: false,
            staticDoc: null
        });
    }

    @Action(SetCurrentDocument)
    setCurrentDocument(sc: StateContext<DocumentViewerObjectStateModel>, action: SetCurrentDocument) {
        const state = sc.getState();
        sc.patchState({
            currentDoc: cloneDeep(action.document),
            report: null,
            currentReportMode: false,
            mergeDoc: null,
            currentReportPage: null,
            reportCollectionConfig: null,
            reportData: null,
            restoreDataMode: false,
            staticDoc: null
        });
    }

    @Action(SetDocumentCollection)
    setDocumentCollection(sc: StateContext<DocumentViewerObjectStateModel>, action: SetDocumentCollection) {
        const state = sc.getState();
        sc.patchState({
            documentCollection: action.collection,
            report: null,
            currentReportMode: false,
            mergeDoc: null,
            staticDoc: null
        });
    }

    @Action(SetCurrentReport)
    setCurrentReport(sc: StateContext<DocumentViewerObjectStateModel>, action: SetCurrentReport) {
        const state = sc.getState();
        const reportCriteria = cloneDeep(action.criteria);
        const currRepPage = cloneDeep(state.currentReportPage);

        sc.patchState({
            report: { reportName: action.reportName, objectCode: action.objectCode, criteria: reportCriteria },
            currentDoc: null, //Clear out currentDoc
            documentCollection: [], //Clear out DocumentCollection
            mergeDoc: null, //Clear out currentMergeDoc
            staticDoc: null
        });
    }

    @Action(SetCurrentReportMode)
    setCurrentReportMode(sc: StateContext<DocumentViewerObjectStateModel>, action: SetCurrentReportMode) {
        const state = sc.getState();
        sc.patchState({
            currentReportMode: action.mode
        });
    }

    @Action(SetMergeDoc)
    setMergeDoc(sc: StateContext<DocumentViewerObjectStateModel>, action: SetMergeDoc) {
        const state = sc.getState();
        const mergeDocCriteria = cloneDeep(action.criteria);
        sc.patchState({
            mergeDoc: {
                docName: action.docName,
                objectCode: action.objectCode,
                criteria: mergeDocCriteria,
                mergeDocFormat: action.mergeDocFormat
            },
            currentDoc: null,
            report: null,
            documentCollection: [],
            currentReportMode: false,
            currentReportPage: null,
            reportCollectionConfig: null,
            reportData: null,
            restoreDataMode: false,
            staticDoc: null
        });
    }

    @Action(SetStaticDoc)
    setStaticDoc(sc: StateContext<DocumentViewerObjectStateModel>, action: SetStaticDoc) {
        const state = sc.getState();
        sc.patchState({
            staticDoc: {
                docName: action.docName,
                objectCode: action.objectCode,
                staticDocFormat: action.staticDocFormat
            },
            currentDoc: null,
            report: null,
            documentCollection: [],
            currentReportMode: false,
            currentReportPage: null,
            reportCollectionConfig: null,
            reportData: null,
            restoreDataMode: false,
            mergeDoc: null
        });
    }

    @Action(SetDocViewerReportData)
    setDocViewerReportData(sc: StateContext<DocumentViewerObjectStateModel>, action: SetDocViewerReportData) {
        const state = sc.getState();
        const reportRowData = cloneDeep(action.rowData);

        sc.patchState({
            reportData: reportRowData
        });
    }

    @Action(SetDocViewerReportCollectionConfig)
    setDocViewerReportCollectionConfig(
        sc: StateContext<DocumentViewerObjectStateModel>,
        action: SetDocViewerReportCollectionConfig
    ) {
        const state = sc.getState();
        const config = cloneDeep(action.collectionConfig);

        sc.patchState({
            reportCollectionConfig: config
        });
    }

    @Action(SetDocViewerRestoreDataMode)
    setDocViewerRestoreDataMode(sc: StateContext<DocumentViewerObjectStateModel>, action: SetDocViewerRestoreDataMode) {
        const state = sc.getState();

        sc.patchState({
            restoreDataMode: action.restore
        });
    }

    @Action(SetCurrentReportPage)
    setCurrentReportPage(sc: StateContext<DocumentViewerObjectStateModel>, action: SetCurrentReportPage) {
        const state = sc.getState();
        const reportPageData = cloneDeep(action.reportPage);

        sc.patchState({
            currentReportPage: reportPageData
        });
    }

    @Action(ResetDocViewerReportState)
    resetDocViewerReportState(sc: StateContext<DocumentViewerObjectStateModel>, action: ResetDocViewerReportState) {
        const state = sc.getState();

        sc.patchState({
            ...state,
            currentReportPage: null,
            reportData: null,
            reportCollectionConfig: null,
            restoreDataMode: false
        });
    }
}
